// src/components/Sections/ProcessSection.tsx
// Process-sektion som visar hur SireusCode arbetar

import React from 'react';
import { Users, Settings, Code, ArrowRight, CheckCircle } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';

interface ProcessStep {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
}

interface ProcessSectionProps {
  className?: string;
}

const ProcessSection: React.FC<ProcessSectionProps> = ({ className = '' }) => {
  const { t } = useAppContext();

  const steps: ProcessStep[] = [
    {
      title: t('processStep1Title'),
      description: t('processStep1Desc'),
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: t('processStep2Title'),
      description: t('processStep2Desc'),
      icon: Settings,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: t('processStep3Title'),
      description: t('processStep3Desc'),
      icon: Code,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: t('processStep4Title'),
      description: t('processStep4Desc'),
      icon: CheckCircle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  return (
    <section id="process" className={`py-16 lg:py-24 bg-white relative ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {t('processTitle')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('processSubtitle')}
          </p>
        </div>

        {/* Process steps */}
        <div className="relative">
          {/* Connection line - hidden on mobile */}
          <div className="hidden lg:block absolute top-24 left-0 w-full h-0.5 bg-gradient-to-r from-blue-200 via-green-200 via-purple-200 to-orange-200"></div>

          {/* Steps grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                {/* Mobile connection line */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden absolute left-8 top-20 w-0.5 h-16 bg-gray-200 z-0"></div>
                )}

                {/* Step card */}
                <div className="relative bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 hover:border-gray-200 z-10">
                  {/* Step number */}
                  <div className="absolute -top-3 -left-3 w-8 h-8 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center text-sm font-bold text-gray-600 shadow-sm">
                    {index + 1}
                  </div>

                  {/* Icon */}
                  <div className={`${step.bgColor} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 relative z-10`}>
                    <step.icon className={`w-8 h-8 ${step.color}`} />
                  </div>

                  {/* Content */}
                  <div className="text-center">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Arrow for desktop */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute -right-4 top-1/2 transform -translate-y-1/2 z-20">
                      <ArrowRight className="w-6 h-6 text-gray-300" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Process benefits */}
        <div className="mt-16 bg-gray-50 rounded-2xl p-8 lg:p-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t('processWhyChooseTitle')}
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('processWhyChooseSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">
                {t('processBenefit1Title')}
              </h4>
              <p className="text-sm text-gray-600">
                {t('processBenefit1Desc')}
              </p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Settings className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">
                {t('processBenefit2Title')}
              </h4>
              <p className="text-sm text-gray-600">
                {t('processBenefit2Desc')}
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">
                {t('processBenefit3Title')}
              </h4>
              <p className="text-sm text-gray-600">
                {t('processBenefit3Desc')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;